import { useRef } from "react";

// The idea is that when we call this function we wait for it to complete before executing it again
// If we try to call it again before it completes, we wait until the first call completes and then execute the callback again immediately after
// If no calls are made while we are waiting for the callback to complete do nothing when it finishes
export function useThrottledQueue<T>(
  fn: () => Promise<T>,
  options?: { minInterval?: number },
) {
  const pending = useRef<Promise<T> | undefined>(undefined);
  const queued = useRef(false);
  const lastExecutionTime = useRef(0);
  const minInterval = options?.minInterval || 0;

  return async () => {
    const now = Date.now();
    const timeSinceLastExecution = now - lastExecutionTime.current;

    // If there's already a call pending, queue this one up
    if (pending.current) {
      queued.current = true;
      await pending.current;

      if (!queued.current) {
        return;
      }
    }

    if (minInterval > 0 && timeSinceLastExecution < minInterval) {
      if (!queued.current) {
        queued.current = true;

        // pause until the minInterval has passed
        await new Promise((resolve) =>
          setTimeout(resolve, minInterval - timeSinceLastExecution),
        );

        if (!queued.current) {
          return;
        }
      } else {
        return;
      }
    }

    queued.current = false;
    lastExecutionTime.current = Date.now();
    pending.current = fn();

    try {
      await pending.current;
    } finally {
      pending.current = undefined;
    }
  };
}
